/*
 * The preview's Get a quote on the quoter (apps/api portal/hub-quote.ts): prices the made-up list with the same shared
 * math, and Book it opens a made-up auto quote page that changes and books like the real one. Nothing is saved.
 */
import {
  addDays,
  firstVisitOf,
  HOURLY_CENTS,
  HOURLY_OFTEN,
  HOURLY_OFTEN_WORDS,
  hourlyQuoteLines,
  hubAnswers,
  extraCents,
  priceHome,
  PRICE_OFTEN,
  PRICE_OFTEN_WORDS,
  quickQuoteLines,
  type HomeAnswers,
  type HourlyAnswers,
  type HubAnswers,
  type PriceList,
  type PublicQuote,
  type QuickQuote,
} from "@steward/shared";

type Answer = Promise<{ status: number; body: unknown }>;
const ok = (body: unknown, status = 200): Answer => Promise.resolve({ status, body });
const fail = (status: number, code: string, message: string): Answer => Promise.resolve({ status, body: { code, message } });

interface HubDemoQuote {
  number: number;
  answers: HubAnswers;
  sent: HubAnswers;
  booked: { date: string; window: string | null } | null;
}
// Book it opens the quote page as a new page load, so the made-up quotes live in this tab's session storage.
const KEY = "steward.preview.hubQuotes";
const made = new Map<string, HubDemoQuote>(load());
let number = 2101 + made.size;
function load(): [string, HubDemoQuote][] {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? "[]") as [string, HubDemoQuote][];
  } catch {
    return [];
  }
}
function save() {
  try {
    sessionStorage.setItem(KEY, JSON.stringify([...made]));
  } catch {
    // Private windows: the quote page shows until the tab reloads.
  }
}

const today = () => new Date().toISOString().slice(0, 10);

// The made-up client had a clean in the last 30 days (the hub shows Keep it this clean), so a plan starts at the plan
// price on the first visit, as on the real server.
function priced(list: PriceList, a: HubAnswers): QuickQuote | null {
  if ("kind" in a) return hourlyQuoteLines(list, a);
  const p = priceHome(list, a);
  return p ? quickQuoteLines(a, p, p.repeat !== null) : null;
}

/** public/my/quoter, public/my/price, public/my/quote, and the made-up quote pages they open (public/quote/hub-N). */
export function hubQuoteRoute(method: string, p: string, body: unknown, list: PriceList, base: () => Answer): Answer | null {
  if (p.startsWith("public/my/quoter"))
    return ok({
      services: list.services.map((s) => ({ id: s.id, label: s.name, repeats: s.repeats })),
      extras: list.extras.map((x) => ({
        id: x.id,
        label: x.name,
        cents: x.priceCents,
        planCents: x.planCents ?? null,
        each: x.each,
        only: x.only,
      })),
      repeats: PRICE_OFTEN.filter((o) => o !== "once").map((o) => ({ id: o, label: PRICE_OFTEN_WORDS[o] })),
      hourlyCents: list.hourlyCents ?? HOURLY_CENTS,
      home: { serviceId: "s1", sizeBy: "rooms", beds: 3, baths: 2, other: 0, sqft: 0, often: "biweekly", extras: {} },
      regularId: "s1",
      planReady: true,
      oneTimes: 1,
    });
  if (p === "public/my/price" || p === "public/my/quote") {
    const parsed = hubAnswers.safeParse((body as { answers?: unknown } | undefined)?.answers);
    if (!parsed.success) return fail(400, "bad_request", "Check your answers.");
    const a = parsed.data;
    const quick = priced(list, a);
    if (p === "public/my/price") {
      if (!quick) return ok({ price: null });
      if ("kind" in a) {
        const each = quick.lines[0]!.unitPriceCents;
        return ok({
          price: {
            title: quick.title,
            firstVisitCents: each,
            repeat: a.often === "once" ? null : { label: HOURLY_OFTEN_WORDS[a.often], perVisitCents: each },
            planStart: false,
          },
        });
      }
      const hp = priceHome(list, a)!;
      const planStart = hp.repeat !== null;
      return ok({
        price: {
          title: quick.title,
          firstVisitCents: firstVisitOf(hp, planStart),
          repeat: hp.repeat ? { label: hp.repeat.label, perVisitCents: hp.repeat.perVisitCents } : null,
          planStart,
        },
      });
    }
    if (!quick) return fail(422, "no_price", "We can't price that here. Text us and we'll help.");
    const token = `hub-${String(number)}`;
    made.set(token, { number, answers: a, sent: a, booked: null });
    number += 1;
    save();
    return ok({ link: `/q/${token}` }, 201);
  }
  const m = /^public\/quote\/(hub-\d+)(?:\/(change|approve))?$/.exec(p);
  if (!m) return null;
  const q = made.get(m[1]!);
  if (!q) return fail(404, "not_found", "That link was not found.");
  if (method === "POST" && m[2] === "change") {
    const ask = (body as { answers?: Record<string, unknown> } | undefined)?.answers ?? {};
    const next = hubAnswers.safeParse({ ...q.answers, ...ask });
    if (!next.success || !priced(list, next.data)) return fail(422, "bad_answers", "Check your choices and try again.");
    q.answers = next.data;
  }
  if (method === "POST" && m[2] === "approve") {
    const book = (body as { book?: { day: string; timeOfDay: string } } | undefined)?.book;
    if (book)
      q.booked = {
        date: book.day,
        window: book.timeOfDay === "afternoon" ? "12 PM to 5 PM" : book.timeOfDay === "morning" ? "8 AM to 12 PM" : null,
      };
  }
  save();
  return base().then((r) => ({ status: 200, body: view(r.body as PublicQuote, q, list) }));
}

/** The made-up quote page: the preview's sent quote page with these answers, lines, choices and open days. */
function view(base: PublicQuote, q: HubDemoQuote, list: PriceList): PublicQuote {
  const quick = priced(list, q.answers)!;
  const a = q.answers;
  const lines = quick.lines.map((l, i) => ({
    id: `hub-line-${String(i)}`,
    description: l.description,
    note: null,
    link: null,
    quantity: 1,
    amountCents: l.unitPriceCents,
    optional: false,
    picked: false,
    recurring: l.recurring,
    optionGroup: null,
    bill: "once" as const,
  }));
  const days = Array.from({ length: 28 }, (_, i) => {
    const date = addDays(today(), i + 1);
    const open = new Date(`${date}T12:00:00`).getDay() !== 0;
    // The made-up cleaner from last time works Monday to Thursday.
    const weekday = new Date(`${date}T12:00:00`).getDay();
    return { date, open, morning: open, afternoon: open && i % 3 !== 1, withCleaner: open && weekday >= 1 && weekday <= 4 };
  });
  return {
    ...base,
    number: q.number,
    firstName: "Ann",
    title: quick.title,
    message: null,
    status: q.booked ? "converted" : "sent",
    lines,
    sections: [],
    sectionFiles: [],
    signature: null,
    payLink: null,
    deposit: null,
    cardRequired: false,
    signRequired: true,
    auto: "kind" in a ? hourlyAuto(list, a) : homeAuto(list, a, q.sent as HomeAnswers),
    booking: q.booked ? null : { cardFirst: false, days, cleaner: "Kenia" },
    bookedFor: q.booked,
  };
}

function hourlyAuto(list: PriceList, a: HourlyAnswers & { kind: "hourly" }): PublicQuote["auto"] {
  return {
    kind: "hourly",
    answers: a,
    rateCents: list.hourlyCents ?? HOURLY_CENTS,
    often: HOURLY_OFTEN.map((o) => ({
      id: o,
      label: HOURLY_OFTEN_WORDS[o],
      cents: hourlyQuoteLines(list, { ...a, often: o })?.lines[0]?.unitPriceCents ?? null,
    })),
  };
}

function homeAuto(list: PriceList, a: HomeAnswers, sent: HomeAnswers): PublicQuote["auto"] {
  const service = list.services.find((x) => x.id === a.serviceId)!;
  const plan = priceHome(list, { ...a, serviceId: "s1", often: "biweekly", extras: {} })?.repeat;
  return {
    kind: "home",
    answers: a,
    sent,
    services: list.services.map((x) => ({
      id: x.id,
      label: x.name,
      cents: priceHome(list, { ...a, serviceId: x.id, often: "once", extras: {} })?.baseCents ?? null,
      repeats: x.repeats,
      sqftOk: x.perSqftCents !== null,
    })),
    often: PRICE_OFTEN.filter((o) => o === "once" || service.repeats).map((o) => ({
      id: o,
      label: PRICE_OFTEN_WORDS[o],
      cents: o === "once" ? null : (priceHome(list, { ...a, often: o })?.repeat?.perVisitCents ?? null),
    })),
    extras: list.extras
      .filter((x) => x.only === null || x.only === a.serviceId)
      .map((x) => {
        const cents = extraCents(x, a.often !== "once" && service.repeats);
        return { id: x.id, label: x.name, cents, listCents: cents === x.priceCents ? null : x.priceCents, each: x.each };
      }),
    planStart: sent.often !== "once",
    // The made-up client booked a one time clean lately, so a one time quote shows the plan price first.
    nudge:
      a.often === "once" && plan
        ? { serviceId: "s1", id: "biweekly", label: plan.label, perVisitCents: plan.perVisitCents }
        : null,
  };
}
